import { useEffect, useMemo, useRef, useState } from "react";
import { advisoryApi, type Json, type Prefill } from "../advisoryApi";
import { isRecord, money, toNumber } from "../format";
import { EstimateNote, PrefillNote, SaveBar, Seg, SourceTag } from "../ui";
import { useSaver, useStepLoad, type StepProps } from "./common";

interface FsField {
  id: string;
  label: string;
  hasMonthlyPayment?: boolean;
}
interface FsData {
  fields: {
    currency: string;
    incomeFields: FsField[];
    assetFields: FsField[];
    liabilityFields: FsField[];
    commitmentFields: FsField[];
  };
  current: { inputData: Json | null } | null;
}
interface Entry {
  id: string;
  typeId: string;
  label: string;
  amount: number;
  monthlyPayment?: number;
  [k: string]: unknown;
}
interface Doc {
  incomeEntries: Entry[];
  assetEntries: Entry[];
  liabilityEntries: Entry[];
  commitmentEntries: Entry[];
  incomeStability?: string;
  monthlyIncome?: number;
  monthlyExpenses?: number;
  liquidityBuffer?: number;
  shortTermExpenses?: number;
  // Anything else QP stored is carried through untouched (a PUT replaces the document).
  rest: Json;
}

type TabKey = "income" | "assets" | "liabilities" | "commitments" | "capacity" | "summary";
const TABS: { key: TabKey; label: string }[] = [
  { key: "income", label: "Income" },
  { key: "assets", label: "Assets" },
  { key: "liabilities", label: "Liabilities" },
  { key: "commitments", label: "Commitments" },
  { key: "capacity", label: "Capacity" },
  { key: "summary", label: "Summary" },
];
const LIST_KEY = {
  income: "incomeEntries",
  assets: "assetEntries",
  liabilities: "liabilityEntries",
  commitments: "commitmentEntries",
} as const;
const OWN_KEYS = new Set([
  "incomeEntries",
  "assetEntries",
  "liabilityEntries",
  "commitmentEntries",
  "incomeStability",
  "monthlyIncome",
  "monthlyExpenses",
  "liquidityBuffer",
  "shortTermExpenses",
  "monthlySurplus",
  "currency",
]);

function entries(v: unknown): Entry[] {
  return Array.isArray(v)
    ? v.filter(isRecord).map((e) => ({ ...(e as Entry), amount: toNumber(e.amount) ?? 0 }))
    : [];
}

function toDoc(input: Json | null | undefined): Doc {
  const d = input ?? {};
  const rest: Json = {};
  for (const [k, v] of Object.entries(d)) if (!OWN_KEYS.has(k)) rest[k] = v;
  const n = (v: unknown) => toNumber(v) ?? undefined;
  return {
    incomeEntries: entries(d.incomeEntries),
    assetEntries: entries(d.assetEntries),
    liabilityEntries: entries(d.liabilityEntries),
    commitmentEntries: entries(d.commitmentEntries),
    incomeStability: typeof d.incomeStability === "string" ? d.incomeStability : undefined,
    monthlyIncome: n(d.monthlyIncome),
    monthlyExpenses: n(d.monthlyExpenses),
    liquidityBuffer: n(d.liquidityBuffer),
    shortTermExpenses: n(d.shortTermExpenses),
    rest,
  };
}

const sum = (list: Entry[]) => list.reduce((a, e) => a + (e.amount || 0), 0);

// Row-id prefixes the server's prefill uses (QP's entry schema has no room for
// a flag, so the id carries the source; QP echoes it back unchanged).
const ANDERSEN_ROW_PREFIX = "andersen-account-";
const DEFAULT_ESTIMATE_PREFIX = "andersen-estimate-";

type Source = "andersen" | "estimate" | null;
function rowSource(id: string, estimatePrefix: string): Source {
  if (id.startsWith(ANDERSEN_ROW_PREFIX)) return "andersen";
  if (id.startsWith(estimatePrefix)) return "estimate";
  return null;
}

/** Nothing saved yet: no stored document, or one without a single figure. */
function isEmptyDoc(input: Json | null | undefined): boolean {
  if (!input) return true;
  const lists = ["incomeEntries", "assetEntries", "liabilityEntries", "commitmentEntries"];
  const anyRows = lists.some((k) => Array.isArray(input[k]) && (input[k] as unknown[]).length > 0);
  const anyScalar = ["monthlyIncome", "monthlyExpenses", "liquidityBuffer", "incomeStability"].some(
    (k) => input[k] !== undefined && input[k] !== null,
  );
  return !anyRows && !anyScalar;
}

interface Estimates {
  keys: Set<string>;
  rowPrefix: string;
  basis: string[];
}
const newId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `e-${Date.now()}-${Math.random()}`);

/**
 * Financial Situation (`financial_situation`) — QP's financial-situation widget:
 * Income / Assets / Liabilities / Commitments / Capacity / Summary. A save
 * replaces the whole document, so every tab's rows travel together.
 *
 * When nothing is saved yet the step opens pre-populated from the Andersen
 * prefill: the client's accounts and cash (Andersen data) plus a demo estimate
 * of income, expenses, housing, debt and buffer (Andersen has none of that).
 * Estimated figures are tagged "Estimate — confirm with the client"; editing
 * one makes it the advisor's own. Nothing reaches Quantfolio until Save.
 */
export function FinancialSituationStep({ sessionId, readOnly, onSaved, prefill, clearPrefill }: StepProps) {
  const load = useStepLoad<FsData>(sessionId, "financial-situation", {});
  const saver = useSaver(onSaved);
  const [tab, setTab] = useState<TabKey>("income");
  const [doc, setDoc] = useState<Doc | null>(null);
  const [base, setBase] = useState("");
  const [prefillUsed, setPrefillUsed] = useState(false);
  // The proposal applied on opening an empty step (the right column's "Use
  // Andersen holdings" arrives as the `prefill` prop instead).
  const [autoPrefill, setAutoPrefill] = useState<Prefill | null>(null);
  const [estimates, setEstimates] = useState<Estimates | null>(null);
  const autoTried = useRef(false);

  const applyProposal = (p: Prefill) => {
    setDoc(toDoc(p.proposal));
    setPrefillUsed(true);
    setEstimates(
      p.estimates
        ? { keys: new Set(p.estimates.keys), rowPrefix: p.estimates.rowPrefix || DEFAULT_ESTIMATE_PREFIX, basis: p.estimates.basis }
        : null,
    );
  };

  useEffect(() => {
    if (!load.data) return;
    const stored = load.data.current?.inputData;
    const d = toDoc(stored);
    setDoc(d);
    setBase(JSON.stringify(d));
    // Open an empty step pre-populated — once per mount, never over saved data,
    // never on a submitted advice, and not when the advisor already asked for a prefill.
    if (readOnly || prefill || autoTried.current || !isEmptyDoc(stored)) return;
    autoTried.current = true;
    let stale = false;
    advisoryApi
      .prefill(sessionId, "financial-situation", {})
      .then((p) => {
        if (stale) return;
        setAutoPrefill(p);
        applyProposal(p);
      })
      .catch(() => {
        /* no proposal — the advisor fills it in by hand */
      });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.data]);

  useEffect(() => {
    if (!prefill) return;
    setAutoPrefill(null);
    applyProposal(prefill);
    setTab("assets");
  }, [prefill]);

  const shownPrefill = prefill ?? autoPrefill;
  const estimatePrefix = estimates?.rowPrefix ?? DEFAULT_ESTIMATE_PREFIX;
  const dismissPrefill = () => {
    setAutoPrefill(null);
    clearPrefill();
  };

  const fields = load.data?.fields;
  const currency = fields?.currency ?? "NOK";
  const typeFields: Record<keyof typeof LIST_KEY, FsField[]> = useMemo(
    () => ({
      income: fields?.incomeFields ?? [],
      assets: fields?.assetFields ?? [],
      liabilities: fields?.liabilityFields ?? [],
      commitments: fields?.commitmentFields ?? [],
    }),
    [fields],
  );

  if (load.loading && !load.data) return <p className="muted">Loading the financial situation…</p>;
  if (load.error) return <div className="banner-error">{load.error}</div>;
  if (!doc) return null;

  const dirty = JSON.stringify(doc) !== base;
  const update = (patch: Partial<Doc>) => setDoc((d) => (d ? { ...d, ...patch } : d));

  async function save() {
    if (!doc) return;
    const clean = (list: Entry[]) => list.filter((e) => e.typeId && Number.isFinite(e.amount));
    const body: Json = {
      ...doc.rest,
      currency,
      incomeEntries: clean(doc.incomeEntries),
      assetEntries: clean(doc.assetEntries),
      liabilityEntries: clean(doc.liabilityEntries),
      commitmentEntries: clean(doc.commitmentEntries),
    };
    for (const k of ["incomeStability", "monthlyIncome", "monthlyExpenses", "liquidityBuffer", "shortTermExpenses"] as const) {
      if (doc[k] !== undefined) body[k] = doc[k];
    }
    if (prefillUsed) body.prefillUsed = true;
    const r = await saver.run(() => advisoryApi.saveStep(sessionId, "financial-situation", {}, body));
    if (r) {
      setBase(JSON.stringify(doc));
      setPrefillUsed(false);
      setAutoPrefill(null);
      // Saved scalars no longer carry a marker; estimated rows keep theirs (by id).
      setEstimates((e) => (e ? { ...e, keys: new Set() } : e));
      clearPrefill();
    }
  }

  const estimatedKey = (k: string) => !!estimates?.keys.has(k);
  const confirmKey = (k: string) =>
    setEstimates((e) => {
      if (!e || !e.keys.has(k)) return e;
      const keys = new Set(e.keys);
      keys.delete(k);
      return { ...e, keys };
    });
  const hasEstimateRows = [doc.incomeEntries, doc.assetEntries, doc.liabilityEntries, doc.commitmentEntries].some((l) =>
    l.some((r) => r.id.startsWith(estimatePrefix)),
  );

  const totals = {
    income: sum(doc.incomeEntries),
    assets: sum(doc.assetEntries),
    liabilities: sum(doc.liabilityEntries),
    commitments: sum(doc.commitmentEntries),
  };

  return (
    <section className="adv-step-card">
      {shownPrefill && <PrefillNote basis={shownPrefill.basis} onDismiss={dismissPrefill} />}
      {(hasEstimateRows || (estimates?.keys.size ?? 0) > 0) && (
        <EstimateNote
          basis={shownPrefill?.estimates?.basis ?? estimates?.basis ?? []}
          saved={!shownPrefill && !dirty}
        />
      )}
      <div className="tabs adv-tabs-tight" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? "tab-link active" : "tab-link"}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab in LIST_KEY && (
        <EntryTable
          kind={tab as keyof typeof LIST_KEY}
          types={typeFields[tab as keyof typeof LIST_KEY]}
          rows={doc[LIST_KEY[tab as keyof typeof LIST_KEY]]}
          currency={currency}
          readOnly={readOnly}
          estimatePrefix={estimatePrefix}
          onChange={(rows) => update({ [LIST_KEY[tab as keyof typeof LIST_KEY]]: rows } as Partial<Doc>)}
        />
      )}

      {tab === "income" && (
        <div className="field">
          <span>
            Income stability {estimatedKey("incomeStability") && <SourceTag source="estimate" />}
          </span>
          <Seg
            value={doc.incomeStability ?? null}
            options={[
              { value: "permanent", label: "Permanent" },
              { value: "temporary", label: "Temporary" },
              { value: "variable", label: "Variable" },
            ]}
            onChange={(v) => {
              update({ incomeStability: v });
              confirmKey("incomeStability");
            }}
            disabled={readOnly}
            label="Income stability"
          />
        </div>
      )}

      {tab === "capacity" && (
        <CapacityTab
          doc={doc}
          currency={currency}
          readOnly={readOnly}
          onChange={update}
          isEstimate={estimatedKey}
          onConfirm={confirmKey}
        />
      )}

      {tab === "summary" && (
        <div className="adv-subsection">
          <div className="perf-strip">
            <Figure label="Annual income" value={money(totals.income, currency)} />
            <Figure label="Assets" value={money(totals.assets, currency)} />
            <Figure label="Liabilities" value={money(totals.liabilities, currency)} />
            <Figure label="Net worth" value={money(totals.assets - totals.liabilities, currency)} tone={totals.assets - totals.liabilities >= 0 ? "up" : "down"} />
          </div>
          <SummaryTable title="Assets by type" rows={doc.assetEntries} types={typeFields.assets} currency={currency} />
        </div>
      )}

      <SaveBar onSave={save} saving={saver.saving} dirty={dirty} error={saver.error} readOnly={readOnly} />
    </section>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: "up" | "down" }) {
  return (
    <div className="perf-figure">
      <span className="label">{label}</span>
      <span className={tone ? `value ${tone}` : "value"}>{value}</span>
    </div>
  );
}

function EntryTable({
  kind,
  types,
  rows,
  currency,
  readOnly,
  estimatePrefix,
  onChange,
}: {
  kind: keyof typeof LIST_KEY;
  types: FsField[];
  rows: Entry[];
  currency: string;
  readOnly: boolean;
  estimatePrefix: string;
  onChange: (rows: Entry[]) => void;
}) {
  const withPayment = kind === "liabilities";
  // Editing an estimated row's figures makes it the advisor's own: it gets a
  // fresh id, which drops the "estimate" tag (in Andersen and in what QP stores).
  const set = (i: number, patch: Partial<Entry>) =>
    onChange(
      rows.map((r, j) => {
        if (j !== i) return r;
        const confirmed = rowSource(r.id, estimatePrefix) === "estimate" && ("amount" in patch || "label" in patch || "monthlyPayment" in patch);
        return { ...r, ...patch, ...(confirmed ? { id: newId(), label: String(patch.label ?? r.label ?? "").replace(/ \(estimate(: none)?\)$/, "") } : {}) };
      }),
    );
  const typeLabel = (id: string) => types.find((t) => t.id === id)?.label ?? id;
  const amountLabel = kind === "income" ? "Annual amount" : kind === "commitments" ? "Monthly amount" : "Amount";

  return (
    <div className="adv-subsection">
      {rows.length === 0 ? (
        <p className="muted">No {kind} recorded yet.</p>
      ) : (
        <table className="table adv-table-compact">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
              <th className="num">
                {amountLabel} ({currency})
              </th>
              {withPayment && <th className="num">Monthly payment</th>}
              {!readOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const hasPayment = types.find((t) => t.id === r.typeId)?.hasMonthlyPayment;
              return (
                <tr key={r.id}>
                  <td>
                    {readOnly ? (
                      typeLabel(r.typeId)
                    ) : (
                      <select className="adv-cell-input" value={r.typeId} onChange={(e) => set(i, { typeId: e.target.value })}>
                        {types.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {readOnly ? (
                      r.label
                    ) : (
                      <input className="adv-cell-input" value={r.label ?? ""} onChange={(e) => set(i, { label: e.target.value })} />
                    )}
                    {rowSource(r.id, estimatePrefix) && <SourceTag source={rowSource(r.id, estimatePrefix)!} />}
                  </td>
                  <td className="num">
                    {readOnly ? (
                      money(r.amount, "")
                    ) : (
                      <input
                        className="adv-amount-input"
                        type="number"
                        min={0}
                        value={Number.isFinite(r.amount) ? r.amount : ""}
                        onChange={(e) => set(i, { amount: toNumber(e.target.value) ?? 0 })}
                      />
                    )}
                  </td>
                  {withPayment && (
                    <td className="num">
                      {hasPayment ? (
                        readOnly ? (
                          money(r.monthlyPayment, "")
                        ) : (
                          <input
                            className="adv-amount-input"
                            type="number"
                            min={0}
                            value={r.monthlyPayment ?? ""}
                            onChange={(e) => set(i, { monthlyPayment: toNumber(e.target.value) ?? undefined })}
                          />
                        )
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  )}
                  {!readOnly && (
                    <td>
                      <div className="cell-actions"><button type="button" className="link-action danger" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                        Remove
                      </button>
                    </div></td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>Total</td>
              <td className="num">{money(sum(rows), currency)}</td>
              {withPayment && <td className="num">{money(rows.reduce((a, r) => a + (r.monthlyPayment ?? 0), 0), currency)}</td>}
              {!readOnly && <td />}
            </tr>
          </tfoot>
        </table>
      )}
      {!readOnly && types.length > 0 && (
        <button
          type="button"
          className="link-action"
          onClick={() => onChange([...rows, { id: newId(), typeId: types[0]!.id, label: types[0]!.label, amount: 0 }])}
        >
          + Add {kind === "income" ? "income" : kind === "assets" ? "an asset" : kind === "liabilities" ? "a liability" : "a commitment"}
        </button>
      )}
    </div>
  );
}

function CapacityTab({
  doc,
  currency,
  readOnly,
  onChange,
  isEstimate,
  onConfirm,
}: {
  doc: Doc;
  currency: string;
  readOnly: boolean;
  onChange: (patch: Partial<Doc>) => void;
  isEstimate: (key: string) => boolean;
  onConfirm: (key: string) => void;
}) {
  const surplus =
    doc.monthlyIncome !== undefined && doc.monthlyExpenses !== undefined ? doc.monthlyIncome - doc.monthlyExpenses : null;
  const num = (k: "monthlyIncome" | "monthlyExpenses" | "liquidityBuffer" | "shortTermExpenses", label: string, hint?: string) => (
    <label className="field">
      <span>
        {label} ({currency}) {isEstimate(k) && <SourceTag source="estimate" />}
      </span>
      <input
        type="number"
        min={0}
        value={doc[k] ?? ""}
        onChange={(e) => {
          onChange({ [k]: toNumber(e.target.value) ?? undefined } as Partial<Doc>);
          onConfirm(k);
        }}
        disabled={readOnly}
      />
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
  const liquid = doc.assetEntries
    .filter((e) => ["cash_savings", "money_market", "stocks_funds", "bonds"].includes(e.typeId))
    .reduce((a, e) => a + e.amount, 0);
  const available = liquid - (doc.liquidityBuffer ?? 0) - (doc.shortTermExpenses ?? 0);
  return (
    <div className="adv-subsection">
      <div className="field-row">
        {num("monthlyIncome", "Monthly income")}
        {num("monthlyExpenses", "Monthly expenses")}
      </div>
      <div className="perf-strip">
        <Figure label="Monthly surplus" value={surplus === null ? "—" : money(surplus, currency)} tone={surplus === null ? undefined : surplus >= 0 ? "up" : "down"} />
        <Figure label="Available for investments" value={money(available, currency)} tone={available >= 0 ? "up" : "down"} />
      </div>
      {surplus !== null && surplus < 0 && <p className="adv-calm-note">Expenses exceed income — the client has a monthly deficit.</p>}
      <div className="field-row">
        {num("liquidityBuffer", "Liquidity buffer", "The reserve the client keeps and does not invest.")}
        {num("shortTermExpenses", "Short-term expenses", "Earmarked for near-term spending.")}
      </div>
    </div>
  );
}

function SummaryTable({ title, rows, types, currency }: { title: string; rows: Entry[]; types: FsField[]; currency: string }) {
  const byType = new Map<string, number>();
  for (const r of rows) byType.set(r.typeId, (byType.get(r.typeId) ?? 0) + r.amount);
  if (byType.size === 0) return null;
  return (
    <table className="table adv-table-compact">
      <thead>
        <tr>
          <th>{title}</th>
          <th className="num">Amount</th>
        </tr>
      </thead>
      <tbody>
        {[...byType.entries()].map(([id, v]) => (
          <tr key={id}>
            <td>{types.find((t) => t.id === id)?.label ?? id}</td>
            <td className="num">{money(v, currency)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          <td className="num">{money(sum(rows), currency)}</td>
        </tr>
      </tfoot>
    </table>
  );
}
